import { Point } from '@/types';

const BYTE = 8; // 8 bits in a byte
const PRIMARY_KEY_SIZE = 8 * BYTE; // 8 bytes for ids
const TINY_INT_SIZE = 1 * BYTE; // 1 byte for tiny integers
const INT_SIZE = 4 * BYTE; // 4 bytes for integers
// const BIG_INT_SIZE = 8 * BYTE; // 8 bytes for big integers
const LOC_ID_LENGTH_SIZE = 2 * BYTE; // 4 bytes for loc_id length
const LOC_NAME_LENGTH_SIZE = 4 * BYTE; // 4 bytes for loc_name length
const DIR_FLOW_LENGTH_SIZE = 1 * BYTE; // 1 byte for dir_flow length
const TSP_NAME_LENGTH_SIZE = 4 * BYTE; // 4 bytes for tsp_name length

export type ObjectToBinaryDefinition = {
  [key: string]: {
    type: 'string' | 'number';
    contentSize: number;
    array?: boolean;
    arrayLengthSize?: number;
  };
};

const POINT_DEFINITION: ObjectToBinaryDefinition = {
  id: { type: 'number', contentSize: PRIMARY_KEY_SIZE },
  node: { type: 'number', contentSize: PRIMARY_KEY_SIZE },
  loc_id: { type: 'string', contentSize: LOC_ID_LENGTH_SIZE },
  loc_name: { type: 'string', contentSize: LOC_NAME_LENGTH_SIZE },
  dir_flo: { type: 'string', contentSize: DIR_FLOW_LENGTH_SIZE },
  tsp_name: { type: 'string', contentSize: TSP_NAME_LENGTH_SIZE },
  tsp_id: { type: 'number', contentSize: PRIMARY_KEY_SIZE },
  capacity_components: {
    type: 'number',
    contentSize: PRIMARY_KEY_SIZE,
    array: true,
    arrayLengthSize: TINY_INT_SIZE,
  },
  notice_components: {
    type: 'number',
    contentSize: PRIMARY_KEY_SIZE,
    array: true,
    arrayLengthSize: TINY_INT_SIZE,
  },
  loc_type_ind: { type: 'string', contentSize: INT_SIZE },
  receipt_zone: { type: 'number', contentSize: PRIMARY_KEY_SIZE },
  delivery_zone: { type: 'number', contentSize: PRIMARY_KEY_SIZE },
  receipt_zone_name: { type: 'string', contentSize: INT_SIZE },
  delivery_zone_name: { type: 'string', contentSize: INT_SIZE },
};

/**
 *
 * @param num number to encode as binary
 * @param size maximum size of the binary representation in bits
 * @returns string representing the number in binary format, padded to the specified size
 * @throws Error if the number is negative or if the size is too small for the number
 */
export const unsignedToBinary = (num: number, size: number) => {
  if (num < 0) {
    throw new Error(
      'unsignedToBinary: Number must be non-negative, got ' + num,
    );
  }
  if (size < Math.ceil(Math.log2(num + 1))) {
    throw new Error(
      `unsignedToBinary: Size ${size} is too small for number ${num}. ` +
        `Minimum required size is ${Math.ceil(Math.log2(num + 1))} bits.`,
    );
  }
  return num.toString(2).padStart(size, '0');
};

/**
 *
 * @param str string to convert to binary
 * @description Converts each character of the string to its binary representation.
 * @returns string representing the binary representation of the input string
 */
export const textToBinary = (str: string = '') => {
  let res = '';
  res = str
    .split('')
    .map((char) => {
      return char.charCodeAt(0).toString(2).padStart(BYTE, '0');
    })
    .join('');
  return res;
};

/**
 *
 * @param binary string representing binary data
 * @description Converts a binary string back to text by interpreting each 8 bits as a character.
 * @returns string representing the original text
 */
export const binaryToText = (binary: string) => {
  return (
    binary
      .match(/.{1,8}/g)
      ?.map((char) => {
        return String.fromCharCode(parseInt(char, 2));
      })
      .join('') ?? ''
  );
};

/**
 *
 * @param str string to convert to binary
 * @param lengthSize number of bits used to store the length of the string
 * @description Converts a string to a binary representation with a variable length prefix.
 * The prefix indicates the length of the string in bytes, allowing for variable-length strings.
 * The length is encoded as an unsigned integer in binary format.
 * @throws Error if the string length exceeds the maximum allowed size based on the lengthSize
 * @returns string representing the binary representation of the input string
 */
const variableLengthStringBytes = (str: string, lengthSize: number) => {
  const strLen = str.length;
  if (strLen > Math.pow(2, lengthSize) - 1) {
    throw new Error(
      `variableLengthStringBytes: String (${str}) length exceeds maximum allowed size of ${
        Math.pow(2, lengthSize) - 1
      } characters.`,
    );
  }
  const lengthBinary = unsignedToBinary(strLen, lengthSize);
  const strBinary = textToBinary(str).padStart(strLen, '0');
  return lengthBinary + strBinary;
};

/**
 *
 * @param binary string representing binary data
 * @param lengthSize number of bits used to store the length of the string
 * @description Converts a binary string back to a variable-length string.
 * The first part of the binary string indicates the length of the string in bits,
 * followed by the actual string data in binary format.
 * @returns tuple containing the number of bits read and the string representation
 */
const variableLengthStringFromBytes = (
  binary: string,
  lengthSize: number,
): [number, string] => {
  let cursor = 0;
  const strLen = parseInt(binary.slice(cursor, cursor + lengthSize), 2);
  cursor += lengthSize;
  const strBinary = binary.slice(cursor, cursor + strLen * BYTE);
  cursor += strLen * BYTE;

  return [cursor, binaryToText(strBinary)];
};

/**
 *
 * @param value string | number | null | undefined
 * @param type 'string' | 'number'
 * @param size number defining the amount of bits to use in case of number encoding and the amount of bits to use to store the string length in case of string encoding
 * @description Converts a primitive value (string or number) to its binary representation.
 * @returns string representing the binary representation of the value
 */
export const primitiveToBinary = (
  value: string | number | null | undefined,
  type: 'string' | 'number',
  size: number = 0,
) => {
  if (type === 'string') {
    return variableLengthStringBytes(value?.toString() ?? '', size);
  } else if (type === 'number') {
    return unsignedToBinary(Number(value ?? 0), size);
  }
  throw new Error(`Unsupported type: ${type}`);
};

/**
 *
 * @param binary string representing binary data
 * @param type 'string' | 'number'
 * @param size number defining the amount of bits to use in case of number encoding and the amount of bits to use to store the string length in case of string encoding
 * @description Converts a binary string back to a primitive value (string or number).
 * @returns tuple containing the number of bits read and the primitive value (string or number)
 */
export const primitiveFromBinary = <
  T extends 'string' | 'number',
  R extends { string: string; number: number }[T],
>(
  binary: string,
  type: T,
  size: number = 0,
): [number, R] => {
  if (type === 'string') {
    const [offset, str] = variableLengthStringFromBytes(binary, size);
    return [offset, str as R];
  } else if (type === 'number') {
    const num = parseInt(binary.slice(0, size), 2);
    return [size, num as R];
  }
  throw new Error(`Unsupported type: ${type}`);
};

/**
 *
 * @param obj object to convert to binary
 * @param objDefinition definition of the object structure, specifying types and sizes for each field
 * @description Converts an object to its binary representation based on the provided definition.
 * @returns string representing the binary representation of the object
 */
export const objToBinary = (
  obj: Record<string, any>,
  objDefinition: ObjectToBinaryDefinition,
) => {
  let binaryRep = '';
  Object.entries(objDefinition).forEach(
    ([
      key,
      { type, contentSize, array = false, arrayLengthSize = TINY_INT_SIZE },
    ]) => {
      const value = obj[key];
      if (array) {
        if (!Array.isArray(value)) {
          throw new Error(`Expected ${key} to be an array`);
        }
        const length = value?.length ?? 0;
        binaryRep += unsignedToBinary(length, arrayLengthSize);
        value.forEach((item) => {
          binaryRep += primitiveToBinary(item, type, contentSize);
        });
      } else {
        binaryRep += primitiveToBinary(value, type, contentSize);
      }
    },
  );
  return binaryRep;
};

/**
 *
 * @param binary string representing binary data
 * @param objDefinition definition of the object structure, specifying types and sizes for each field
 * @description Converts a binary string back to an object based on the provided definition.
 * @returns object of type T constructed from the binary data
 */
export const objFromBinary = <T extends Record<string, any>>(
  binary: string,
  objDefinition: ObjectToBinaryDefinition,
): T => {
  let cursor = 0;
  const obj: Record<string, any> = {};

  Object.entries(objDefinition).forEach(
    ([
      key,
      { type, contentSize, array = false, arrayLengthSize = TINY_INT_SIZE },
    ]) => {
      if (array) {
        const length = parseInt(
          binary.slice(cursor, cursor + arrayLengthSize),
          2,
        );
        cursor += arrayLengthSize;
        const items: any[] = [];
        for (let i = 0; i < length; i++) {
          const [offset, item] = primitiveFromBinary(
            binary.slice(cursor),
            type,
            contentSize,
          );
          cursor += offset;
          items.push(item);
        }
        obj[key] = items as any;
      } else {
        const [offset, item] = primitiveFromBinary(
          binary.slice(cursor),
          type,
          contentSize,
        );
        obj[key] = item;
        cursor += offset;
      }
    },
  );

  return obj as T;
};

export const pointToBinary = (point: Point) => {
  const binary = objToBinary(point, POINT_DEFINITION);
  return binaryToText(binary);
};

export const binaryToPoint = (binary: string): Partial<Point> => {
  const bits = textToBinary(binary);
  const defaultPoint: Point = {
    id: 0,
    node: 0,
    loc_id: '',
    loc_name: '',
    dir_flo: '',
    tsp_name: '',
    tsp_short_name: '',
    tsp_id: 0,
    capacity_components: [],
    flow_activity: null,
    flow_direction: null,
    notice_components: [],
    notice_activity: null,
    complete: false,
    inact_date: null,
    loc_type_ind: null,
    receipt_zone: null,
    delivery_zone: null,
    compressor: false,
    receipt_zone_name: '',
    delivery_zone_name: '',
    verified: false,
    name: '',
    needs_review: false,
    review_notes: '',
    tsp_ferc_cid: null,
    up_dn_ferc_cid: null,
    up_dn_name: null,
    up_dn_loc_name: null,
    loc_cnty: null,
    seg_nbr: null,
    price_components: [],
  };
  const data = objFromBinary<Partial<Point>>(bits, POINT_DEFINITION);
  const point: Point = {
    ...defaultPoint,
    ...data,
    name: data.loc_name ?? '',
  };
  return point;
};

export const pointDictToBinary = (points: Record<number | string, Point>) => {
  let binary = '';
  Object.values(points).forEach((point) => {
    const pointRep = pointToBinary(point);
    const pointLength = binaryToText(
      unsignedToBinary(pointRep.length, INT_SIZE),
    );
    binary += pointLength + pointRep;
  });
  return binary;
};

export const binaryToPointDict = (
  binary: string,
): Record<number | string, Point> => {
  const bits = binary;
  let cursor = 0;
  const points: Record<number | string, Point> = {};

  while (cursor < bits.length) {
    const pointLength = parseInt(
      textToBinary(bits.slice(cursor, cursor + INT_SIZE / BYTE)),
      2,
    );
    cursor += INT_SIZE / BYTE;

    const pointBinary = bits.slice(cursor, cursor + pointLength);
    cursor += pointLength;

    const point = binaryToPoint(pointBinary);
    points[point.id ?? ''] = point as Point; // Cast to Point to ensure type consistency
  }

  return points;
};
